import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetPositionsQuery } from '@app/api/Positions';
import { EDIT_POSITION_PATH, WARNING } from '@app/constants';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NPositions } from '@app/types/Positions';

export const usePositions = () => {
  const { activeBranchId } = useUserPrint();

  const {
    userAccessRights: { Positions },
  } = useNewUserAccessRights();

  const hasUserRightToViewPositions = useMemo(
    () => Positions?.IsOn,
    [Positions]
  );
  const hasUserRightToCreatePosition = useMemo(
    () => Positions?.IsCreate,
    [Positions]
  );
  const hasUserRightToEditPosition = useMemo(
    () => Positions?.IsEdit,
    [Positions]
  );
  const hasUserRightToDeletePosition = useMemo(
    () => Positions?.IsDelete,
    [Positions]
  );

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [positions, setPositions] = useState<NPositions.ModelPosition[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { showNotification } = useContext(NotificationContext);
  const { isMobile } = useWindowSize();

  const [getPositions] = useLazyGetPositionsQuery();

  const getPositionsHandler = useCallback(async () => {
    try {
      setIsLoading(true);

      const result = await getPositions({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setPositions(result);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      setIsLoading(false);
    }
  }, [activeBranchId, showNotification, getPositions]);

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${EDIT_POSITION_PATH}?id=${id}`);
    },
    [navigate]
  );

  const handleChange = useCallback((page: number, pageSize: number) => {
    setPageNumber(page);
    setRowsPerPage(pageSize);
  }, []);

  return {
    isLoading,
    positions,
    handleEdit,
    isMobile,
    hasUserRightToViewPositions,
    hasUserRightToCreatePosition,
    hasUserRightToEditPosition,
    hasUserRightToDeletePosition,
    handleChange,
    pageNumber,
    rowsPerPage,
    getPositionsHandler,
  };
};
